import type { AnalyticsEventPayload } from '@atlaskit/analytics-next/AnalyticsEvent';
import type { AuthorityUiMetadata } from '@atlassian/conversation-assistant-service-api/types/assistance-service';

/**
 * Derives non-UGC authority attributes for the authoritative-answer panel-shown
 * event. Only counts, booleans, and the coarse authority level/confidence are
 * emitted — never source titles, expert names, URLs, or reasoning text.
 */
const getAuthorityAttributes = (authorityUiMetadata?: AuthorityUiMetadata | null) => {
	if (!authorityUiMetadata) {
		return { hasAuthoritativeSources: false };
	}
	return {
		hasAuthoritativeSources: true,
		authorityLevel: authorityUiMetadata.authorityLevel,
		isHighlyAuthoritative:
			authorityUiMetadata.isHighlyAuthoritative ?? authorityUiMetadata.highlyAuthoritative ?? false,
		overallConfidence: authorityUiMetadata.overallConfidence,
		authoritativeSourceCount: authorityUiMetadata.sources?.length ?? 0,
		authoritativeExpertCount: authorityUiMetadata.domainExperts?.length ?? 0,
	};
};

/**
 * Builds the shared "authoritative answer panel shown" analytics event fired
 * when the attribution panel is opened. Both Chat and SAIN use this single
 * builder so the event shape and authority attributes stay identical; each
 * surface passes its own `product` and correlation attributes (SAIN:
 * searchSessionId/queryHash/traceId; Chat: conversationId/messageId) so the
 * event can be deduped against that surface's other answer events.
 */
export const buildAuthoritativeAnswerPanelShownEvent = ({
	product,
	source,
	correlationAttributes,
	authorityUiMetadata,
}: {
	product: 'chat' | 'sain';
	/** Optional GASv3 `source` (screen). Chat omits this. */
	source?: string;
	/** Non-UGC correlation ids specific to the surface. */
	correlationAttributes?: Record<string, string | undefined>;
	authorityUiMetadata?: AuthorityUiMetadata | null;
}): AnalyticsEventPayload => ({
	action: 'shown',
	actionSubject: 'authoritativeAnswerPanel',
	...(source ? { source } : {}),
	eventType: 'track',
	attributes: {
		product,
		...correlationAttributes,
		...getAuthorityAttributes(authorityUiMetadata),
	},
});
